/**
 * The wire shapes of the control plane API.
 *
 * Zod is the source of truth and the OpenAPI document is generated from it,
 * not the other way round. The server already validates with these schemas,
 * so a hand-written spec could describe something the code does not actually
 * enforce - and that gap is invisible until a consumer trusts the wrong half.
 * Generating means the two cannot disagree.
 *
 * Dates cross the wire as ISO strings, never Date objects. JSON has no date
 * type, so a schema claiming `z.date()` would describe something that can
 * never arrive.
 */
import { z } from 'zod';
import { BackendDescription } from './backend.js';
export const ROLES = ['owner', 'admin', 'developer', 'billing', 'viewer'];
export const Role = z.enum(ROLES);
export const DATABASE_TIERS = ['d1', 'neon'];
export const DatabaseTier = z.enum(DATABASE_TIERS);
export const API_KEY_TYPES = ['anon', 'service'];
export const ApiKeyType = z.enum(API_KEY_TYPES);
export const INVITATION_STATES = ['pending', 'accepted', 'revoked', 'expired'];
export const InvitationState = z.enum(INVITATION_STATES);
const Uuid = z.string().uuid();
const IsoDateTime = z.string().datetime();
// --- entities ---------------------------------------------------------------
export const Project = z.object({
    id: Uuid,
    orgId: Uuid,
    name: z.string(),
    slug: z.string(),
    databaseTier: DatabaseTier,
    status: z.string(),
    region: z.string(),
    /**
     * Where this project's own API lives: https://<ref>.<data-plane host>.
     * Null until the environment has a data plane configured.
     */
    dataPlaneUrl: z.string().url().nullable(),
    createdAt: IsoDateTime,
});
export const ApiKey = z.object({
    id: Uuid,
    projectId: Uuid,
    name: z.string(),
    type: ApiKeyType,
    /**
     * Enough to recognise the key in a list, never enough to reconstruct it.
     * The full key exists only in the response to the create call.
     */
    keyPrefix: z.string(),
    lastUsedAt: IsoDateTime.nullable(),
    expiresAt: IsoDateTime.nullable(),
    revokedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
});
export const Member = z.object({
    userId: Uuid,
    email: z.string(),
    name: z.string().nullable(),
    role: Role,
    joinedAt: IsoDateTime,
});
export const Invitation = z.object({
    id: Uuid,
    email: z.string(),
    role: Role,
    state: InvitationState,
    invitedByUserId: Uuid.nullable(),
    expiresAt: IsoDateTime,
    acceptedAt: IsoDateTime.nullable(),
    revokedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
});
export const Organization = z.object({
    id: Uuid,
    name: z.string(),
    slug: z.string(),
    /** The caller's role in it. Organizations you cannot act in are not listed. */
    role: Role,
    /** Created for them at sign-up rather than by them. */
    isPersonal: z.boolean(),
    createdAt: IsoDateTime,
});
export const CreateOrganizationBody = z.object({
    name: z.string().min(1).max(100),
    slug: z.string().min(3).max(63).optional(),
});
/** An invitation addressed to the CALLER's email, shown before they join. */
export const PendingInvitation = z.object({
    id: Uuid,
    orgId: Uuid,
    orgName: z.string(),
    role: Role,
    expiresAt: IsoDateTime,
});
export const Me = z.object({
    authorization: z.object({ via: z.enum(['session', 'token', 'oauth']), projectId: z.string().uuid().nullable() }).optional(),
    user: z.object({ id: Uuid, clerkUserId: z.string() }),
    organization: z
        .object({ id: Uuid, name: z.string(), slug: z.string(), role: Role })
        .nullable(),
    permissions: z.array(z.string()),
});
export const Health = z.object({
    status: z.string(),
    service: z.string(),
    environment: z.string(),
    revision: z.string(),
    uptimeSeconds: z.number(),
    /**
     * Operational readiness, published because the alternative was probing a
     * customer's Worker and inferring from a 404. Counts and short codes only:
     * this endpoint is public.
     */
    dataPlane: z.record(z.string(), z.unknown()).optional(),
    authWorkers: z.record(z.string(), z.unknown()).optional(),
    orphanSweep: z.record(z.string(), z.unknown()).optional(),
    logs: z.record(z.string(), z.unknown()).optional(),
});
/**
 * Paging a user directory.
 *
 * `cursor` is opaque on purpose - it is the created_at of the last row on
 * the previous page, and a caller that starts computing them will be broken
 * by any change to the ordering.
 */
/**
 * A vector search.
 *
 * `text` or `vector`, and refusing both-or-neither is the point: sending
 * text AND a pre-computed embedding is a request with two different
 * questions in it, and answering either one silently is worse than saying
 * which was meant.
 */
export const VectorQueryBody = z.object({
    text: z.string().min(1).optional(),
    vector: z.array(z.number()).optional(),
    topK: z.number().int().min(1).max(100).optional(),
    filter: z.record(z.string(), z.unknown()).optional(),
}).refine((b) => (b.text === undefined) !== (b.vector === undefined), { message: 'send exactly one of "text" or "vector"' });
/** Entries of { id, text | values, metadata? }. Same one-of rule. */
export const VectorUpsertBody = z.object({
    vectors: z.array(z.object({
        id: z.string().min(1),
        text: z.string().min(1).optional(),
        values: z.array(z.number()).optional(),
        metadata: z.record(z.string(), z.unknown()).optional(),
    }).refine((v) => (v.text === undefined) !== (v.values === undefined), { message: 'each vector needs exactly one of "text" or "values"' })).min(1).max(100),
});
/**
 * A realtime channel.
 *
 * The same alphabet the data plane accepts. Narrow here as well as there:
 * the channel is signed INTO the ticket, so a ticket for one channel
 * cannot open another, and a channel this side would not accept is a
 * ticket nobody can use.
 */
export const RealtimeTicketBody = z.object({
    channel: z.string().regex(/^[a-zA-Z0-9_:.-]{1,128}$/),
});
export const AuthUsersQuery = z.object({
    limit: z.coerce.number().int().min(1).max(100).optional(),
    cursor: z.string().optional(),
    search: z.string().max(200).optional(),
});
export const AuthUserParams = z.object({
    id: z.string().uuid(),
    /**
     * An end user's id, in the alphabet the auth service generates. Narrow
     * here as well as in the service: the id reaches a SQL statement, and a
     * contract that accepts anything makes the guard downstream the only one.
     */
    userId: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
});
// --- request bodies ---------------------------------------------------------
export const CreateProjectBody = z.object({
    name: z.string().min(1).max(100),
    slug: z.string().min(3).max(63).optional(),
    // Existing project responses retain the broader tier type; new projects
    // can only request a provider that the provisioning saga actually supports.
    databaseTier: z.literal('d1', { errorMap: () => ({ message: 'Only d1 is available. Neon Postgres is not available yet.' }) }).default('d1'),
    region: z.string().max(32).optional(),
    planId: Uuid.optional(),
    factsFingerprint: z.string().regex(/^[a-f0-9]{64}$/).optional(),
}).strict();
/**
 * First-run orchestration. A project reference means "use this existing
 * project"; a name means "create a new project". Sending neither asks the
 * server what choices exist. Sending both is ambiguous and is refused.
 */
export const BootstrapBody = z.object({
    project: z.string().min(1).max(100).optional(),
    name: z.string().min(1).max(100).optional(),
    slug: z.string().min(3).max(63).optional(),
    /** Stable across retries; generated and persisted before creation starts. */
    requestId: Uuid.optional(),
    planId: Uuid.optional(),
    factsFingerprint: z.string().regex(/^[a-f0-9]{64}$/).optional(),
}).strict().refine((body) => !(body.project && body.name), {
    message: 'send either project or name, not both',
}).refine((body) => !body.slug || Boolean(body.name), {
    message: 'slug is only valid when creating a named project',
}).refine((body) => !body.name || Boolean(body.requestId), {
    message: 'requestId is required when creating a named project',
}).refine((body) => !body.requestId || Boolean(body.name), {
    message: 'requestId is only valid when creating a named project',
}).refine((body) => !(body.planId || body.factsFingerprint) || Boolean(body.name), {
    message: 'planId and factsFingerprint are only valid when creating a named project',
});
/** Public frontend key only; accepting a caller-selected type would widen OAuth. */
export const CreateApplicationKeyBody = z.object({
    name: z.string().min(1).max(100),
}).strict();
export const CreateApiKeyBody = z.object({
    name: z.string().min(1).max(100),
    type: ApiKeyType,
});
/**
 * A personal access token, as it appears in a list. The token itself is
 * never here - only the prefix, which is enough to recognise one and not
 * enough to use it.
 */
export const AccessToken = z.object({
    id: Uuid,
    name: z.string(),
    tokenPrefix: z.string(),
    orgId: Uuid,
    lastUsedAt: IsoDateTime.nullable(),
    expiresAt: IsoDateTime.nullable(),
    revokedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
});
export const CreateAccessTokenBody = z.object({
    name: z.string().min(1).max(100),
    /** Omitted means it does not expire until revoked. */
    expiresInDays: z.number().int().min(1).max(365).optional(),
    /**
     * 'read_only' narrows the token to reads, whatever its owner may do.
     * Omitted means 'full', so an older client keeps working unchanged.
     */
    scope: z.enum(['full', 'read_only']).optional(),
});
/**
 * Project data operations.
 *
 * The bodies are validated here; the RESPONSES are passed through from the
 * project's own API without reshaping, which is why they are typed loosely.
 * Restating the Worker's response shape in this package would be a second
 * definition of it that could disagree with the first - and the Worker is
 * the thing that actually produces it.
 */
export const RunSqlBody = z.object({
    sql: z.string().min(1).max(100_000),
    /** Values for ? placeholders. Never interpolate into the statement. */
    params: z.array(z.unknown()).max(100).default([]),
});
export const SchemaBody = z.object({
    schema: z.unknown(),
    /**
     * Which schema this is, so the server can tell "I am removing a table I
     * used to declare" from "a different schema has never heard of it". The
     * two produce an identical request otherwise. Optional: omitting it
     * records no ownership and behaves as before.
     */
    lineage: z.string().min(1).max(200).optional(),
});
export const ApplySchemaBody = z.object({
    schema: z.unknown(),
    /**
     * Required for anything that drops a table or a column. Defaults to false,
     * and only the boolean true counts - a truthy string is not consent.
     */
    allowDestructive: z.boolean().default(false),
    /**
     * Which schema this is, so the server can tell "I am removing a table I
     * used to declare" from "a different schema has never heard of it". The
     * two produce an identical request otherwise. Optional: omitting it
     * records no ownership and behaves as before.
     */
    lineage: z.string().min(1).max(200).optional(),
    /**
     * The schema revision the plan behind this apply was computed against.
     * The apply is refused if the database has moved since, because the
     * operations it would run are then not the ones anybody reviewed.
     * Optional: omitting it claims nothing and behaves as before.
     */
    expectedRevision: z.string().min(1).max(128).optional(),
});
/**
 * Social sign-in credentials for one provider.
 *
 * The client id is not a secret - it travels in the redirect URL the
 * browser makes - and the client secret is. Neither is ever returned.
 */
export const AuthProviderBody = z.object({
    clientId: z.string().min(1).max(512),
    clientSecret: z.string().min(1).max(512),
});
/**
 * A logical bucket's policy.
 *
 * `adopt` is a deliberate second step: registering a bucket over objects
 * that already exist applies a policy to files nobody chose one for, so it
 * is refused once and asked for explicitly.
 */
export const StorageBucketBody = z.object({
    visibility: z.enum(['private', 'public']).default('private'),
    ownerScoped: z.boolean().default(false),
    adopt: z.boolean().optional(),
});
/**
 * A policy, as written. The expression itself is passed through unvalidated
 * here on purpose: the project's own Worker validates it against that
 * table's real columns, which is the only place that knowledge exists.
 * Restating the grammar here would be a second definition to drift.
 */
export const PolicyBody = z.object({
    table: z.string().min(1).max(63),
    role: z.enum(['anon', 'authenticated']),
    operation: z.enum(['select', 'insert', 'update', 'delete']),
    name: z.string().min(1).max(64),
    using: z.unknown().optional(),
    check: z.unknown().optional(),
});
/** A function's source. Validated for size here; for syntax by Cloudflare. */
export const DeployFunctionBody = z.object({
    name: z.string().min(1).max(31),
    source: z.string().min(1).max(1_000_000),
});
/**
 * A secret goes in; nothing ever comes back out. There is deliberately no
 * response schema carrying a value.
 */
export const SetFunctionSecretBody = z.object({
    name: z.string().min(1).max(64),
    value: z.string().min(1).max(5120),
});
/**
 * A static site's manifest: path to { hash, type, size }.
 *
 * The file map is passthrough rather than a strict record because the data
 * plane validates it properly - path shape, hash shape, per-file size - and
 * duplicating those rules here would give two sources of truth that drift.
 * What this schema is for is rejecting obvious nonsense before a round trip.
 */
export const SiteManifestBody = z.object({
    singlePageApp: z.boolean().optional(),
    files: z.record(z.string(), z.object({
        hash: z.string(),
        type: z.string().optional(),
        size: z.number().int().nonnegative(),
    })),
});
/** null takes a function off its schedule. */
export const SetFunctionScheduleBody = z.object({
    schedule: z.string().min(1).max(200).nullable(),
});
export const EmailPreviewQuery = z.object({
    kind: z.enum(['password_reset', 'email_verification']).default('password_reset'),
});
export const SetAuthUserDisabledBody = z.object({
    disabled: z.boolean(),
    /**
     * Why, for whoever reads the audit trail later.
     *
     * Never shown to the person it is about: they meet a fixed message from
     * the auth Worker. A note written for an operator is not a note written
     * for the customer's user, and sending it to them by accident is how a
     * private remark becomes a support incident.
     */
    reason: z.string().max(200).optional(),
});
export const EnableAuthBody = z.object({
    /**
     * Origins the customer's app is served from. Empty means none trusted,
     * which is the safe default - a permissive list is somebody's CSRF.
     */
    trustedOrigins: z.array(z.string().url()).max(20).optional(),
});
export const CreateInvitationBody = z.object({
    email: z.string().min(3).max(254),
    role: Role,
});
// --- responses --------------------------------------------------------------
export const ProjectResponse = z.object({ project: Project });
export const ProjectListResponse = z.object({ projects: z.array(Project) });
export const BootstrapNextAction = z.object({
    code: z.string(),
    command: z.string(),
    description: z.string(),
});
export const BootstrapOperation = z.object({
    requestId: Uuid,
    status: z.enum(['creating', 'provisioning', 'ready', 'failed']),
    attempts: z.number().int().nonnegative(),
    failure: z.object({ code: z.string(), message: z.string() }).nullable(),
});
export const BootstrapResponse = z.discriminatedUnion('status', [
    z.object({
        status: z.enum(['ready', 'provisioning', 'attention_required']),
        project: Project,
        backend: BackendDescription,
        nextActions: z.array(BootstrapNextAction),
        operation: BootstrapOperation.optional(),
    }),
    z.object({
        status: z.literal('choice_required'),
        projects: z.array(Project),
        nextActions: z.array(BootstrapNextAction),
    }),
    z.object({
        status: z.literal('recovering'),
        operation: BootstrapOperation,
        nextActions: z.array(BootstrapNextAction),
    }),
]);
export const MemberListResponse = z.object({ members: z.array(Member) });
export const ApiKeyListResponse = z.object({ keys: z.array(ApiKey) });
export const InvitationListResponse = z.object({ invitations: z.array(Invitation) });
/** The plaintext appears here exactly once and is never retrievable again. */
export const IssuedApiKeyResponse = z.object({
    key: ApiKey.extend({ plaintext: z.string() }),
    warning: z.string(),
});
export const AccessTokenListResponse = z.object({ tokens: z.array(AccessToken) });
/** The plaintext appears here exactly once and is never retrievable again. */
export const IssuedAccessTokenResponse = z.object({
    token: AccessToken.extend({ plaintext: z.string() }),
    warning: z.string(),
});
/** Passed straight through from the project's own API. */
/**
 * One turn of a conversation with a model.
 *
 * Deliberately not a passthrough of whatever a provider accepts. The set
 * of knobs here is the set the gateway can honour on EVERY model it
 * routes to, which is what makes a model name swappable rather than a
 * rewrite.
 */
export const AiChatBody = z.object({
    messages: z.array(z.object({
        role: z.enum(['system', 'user', 'assistant']),
        content: z.string(),
    })).min(1),
    model: z.string().optional(),
    maxTokens: z.number().int().positive().optional(),
    temperature: z.number().min(0).max(2).optional(),
});
/**
 * Connecting a domain the customer already owns.
 *
 * Only the name. Everything else about a custom domain - which records to
 * create, what state it is in - is ours to work out and theirs to read,
 * not theirs to supply.
 */
export const AddDomainBody = z.object({
    domain: z.string().min(1).max(253),
});
export const PassthroughResponse = z.record(z.string(), z.unknown());
export const IssuedInvitationResponse = z.object({
    invitation: Invitation,
    token: z.string(),
});
/**
 * One error shape for every failure. `error` is a stable machine code that
 * clients may branch on; `message` is written for a person and may change
 * wording without being a breaking change.
 */
export const ErrorResponse = z.object({
    error: z.string(),
    message: z.string().optional(),
    detail: z.unknown().optional(),
});
export const OrganizationResponse = z.object({
    organization: z.object({ id: Uuid, name: z.string(), slug: z.string(), role: Role }).nullable(),
});
export const ProjectStateResponse = z.object({
    project: z.object({ id: Uuid, status: z.string() }),
});
export const OrganizationListResponse = z.object({ organizations: z.array(Organization) });
export const CreatedOrganizationResponse = z.object({ organization: Organization });
export const PendingInvitationListResponse = z.object({ invitations: z.array(PendingInvitation) });
export const RedeemedInvitationResponse = z.object({
    organization: z.object({ id: Uuid, role: Role }),
});
/** One consent/refresh family, never a credential. */
export const OAuthConnection = z.object({
    id: z.string().uuid(), clientId: z.string().uuid(), clientName: z.string(), orgId: z.string().uuid(),
    projectId: z.string().uuid().nullable(), projectName: z.string().nullable(),
    scopes: z.array(z.string()), status: z.enum(['ready', 'awaiting_exchange']),
    connectedAt: z.string().datetime(), expiresAt: z.string().datetime(), lastUsedAt: z.string().datetime().nullable(),
});
export const OAuthConnectionListResponse = z.object({ enabled: z.boolean(), connections: z.array(OAuthConnection) });
//# sourceMappingURL=schemas.js.map